import * as React from 'react';
import { API, ApiError, refreshAccessToken } from '@pilot/api';

interface SessionUser {
  id: string;
  email: string;
  name: string;
  image?: string;
  avatar?: string;
  role: string;
}
interface Session { user: SessionUser }
type Status = 'loading' | 'authenticated' | 'unauthenticated';
const SessionContext = React.createContext<{ data: Session | null; status: Status }>({ data: null, status: 'loading' });

function toSession(user: any): Session {
  return { user: { id: user.id, email: user.email, name: user.fullName ?? user.name, image: user.avatarUrl ?? user.image ?? undefined, avatar: user.avatarUrl ?? user.avatar ?? undefined, role: user.role } };
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [value, setValue] = React.useState<{ data: Session | null; status: Status }>({ data: null, status: 'loading' });
  const load = React.useCallback(async () => {
    try {
      await refreshAccessToken();
      const result = await API.auth.getSession();
      setValue({ data: toSession(result.user), status: 'authenticated' });
    } catch { setValue({ data: null, status: 'unauthenticated' }); }
  }, []);
  React.useEffect(() => {
    void load();
    const listener = () => void load();
    window.addEventListener('pilot:session', listener);
    return () => window.removeEventListener('pilot:session', listener);
  }, [load]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() { return React.useContext(SessionContext); }

export async function signIn(provider: string, options: Record<string, any> = {}) {
  try {
    if (provider === 'credentials') {
      await API.auth.signIn({ email: options.email, password: options.password });
      window.dispatchEvent(new Event('pilot:session'));
      if (options.redirect !== false) window.location.assign(options.callbackUrl ?? '/spaces');
      return { ok: true, error: null };
    }
    const result = await API.auth.oauthStart(provider as 'google' | 'apple');
    window.location.assign(result.authorizationUrl);
    return { ok: true, error: null };
  } catch (error) {
    return { ok: false, error: error instanceof ApiError ? error.message : 'SignInFailed' };
  }
}

export async function signOut(options: { callbackUrl?: string; redirect?: boolean } = {}) {
  await API.auth.signOut().catch(() => undefined);
  window.dispatchEvent(new Event('pilot:session'));
  if (options.redirect !== false) window.location.assign(options.callbackUrl ?? '/auth/sign-in');
}
